/**
 * Registry of public legal documents rendered at /legal/<slug>.
 *
 * Source markdown lives in docs/legal/ at repo root and is imported here as
 * raw text via Vite's `?raw` query suffix. This keeps the canonical source in
 * one place (versioned, reviewable in PRs) while the marketing surface renders
 * the same content publicly.
 *
 * Adding a new legal document:
 *   1. Drop the .md file into docs/legal/
 *   2. Add a registry entry below
 *   3. Add the slug → import to LEGAL_DOCS
 *   4. Add the link in Footer.tsx
 */

import { stripPrologue } from './utils';

// Vite ?raw imports outside src — typed via vite/client.
import termsMarkdown from '../../../../../docs/legal/terms-of-service.md?raw';
import privacyMarkdown from '../../../../../docs/legal/privacy-policy.md?raw';
import refundMarkdown from '../../../../../docs/legal/refund-policy.md?raw';
import aiDisclosureMarkdown from '../../../../../docs/legal/ai-disclosure-statement.md?raw';
import cookiesMarkdown from '../../../../../docs/legal/cookie-policy.md?raw';

export type LegalSlug = 'terms' | 'privacy' | 'refund' | 'ai-disclosure' | 'cookies';

export type LegalDocMeta = {
  slug: LegalSlug;
  title: string;             // full doc title
  shortTitle: string;        // for footer / cross-links
  number: string;            // race-bib-style index ("01", "02", ...)
  effective: string;         // ISO-ish date string for metadata strip
  partOf?: string;           // optional "Part of <Terms of Service>" hint
  showToc: boolean;          // long docs get sticky TOC; short ones don't
  markdown: string;          // raw markdown content
};

export const LEGAL_DOCS: Record<LegalSlug, LegalDocMeta> = {
  terms: {
    slug: 'terms',
    title: 'Terms of Service',
    shortTitle: 'Terms',
    number: '01',
    effective: 'May 4, 2026',
    showToc: true,
    markdown: stripPrologue(termsMarkdown as string),
  },
  privacy: {
    slug: 'privacy',
    title: 'Privacy Policy',
    shortTitle: 'Privacy',
    number: '02',
    effective: 'May 4, 2026',
    partOf: 'Terms of Service',
    showToc: true,
    markdown: stripPrologue(privacyMarkdown as string),
  },
  refund: {
    slug: 'refund',
    title: 'Refund Policy',
    shortTitle: 'Refund',
    number: '03',
    effective: 'May 4, 2026',
    partOf: 'Terms of Service',
    showToc: false,
    markdown: stripPrologue(refundMarkdown as string),
  },
  'ai-disclosure': {
    slug: 'ai-disclosure',
    title: 'AI Disclosure Statement',
    shortTitle: 'AI Disclosure',
    number: '04',
    effective: 'May 4, 2026',
    partOf: 'Terms of Service',
    showToc: true,
    markdown: stripPrologue(aiDisclosureMarkdown as string),
  },
  cookies: {
    slug: 'cookies',
    title: 'Cookie Policy',
    shortTitle: 'Cookies',
    number: '05',
    effective: 'May 4, 2026',
    partOf: 'Privacy Policy',
    showToc: false,
    markdown: stripPrologue(cookiesMarkdown as string),
  },
};

export const LEGAL_SLUGS: LegalSlug[] = [
  'terms',
  'privacy',
  'refund',
  'ai-disclosure',
  'cookies',
];

export function isLegalSlug(value: string): value is LegalSlug {
  return (LEGAL_SLUGS as string[]).includes(value);
}

/**
 * Parse window.location.pathname into a legal slug, or null if the path
 * doesn't match the /legal/<slug> shape.
 */
export function parseLegalPath(pathname: string): LegalSlug | 'unknown' | null {
  const match = /^\/legal\/([^/]+)\/?$/.exec(pathname);
  if (!match) return null;
  const slug = match[1];
  return isLegalSlug(slug) ? slug : 'unknown';
}
